import React, { useState } from 'react';
import { useTranslation } from '../../context/TranslationContext';

// Inline "No Image" placeholder. The old https://via.placeholder.com service is dead
// (the host no longer resolves) and the onError handler below kept re-setting that same
// dead URL, so a failed image retried forever — a request loop that slowed the page.
// A data URI needs no network, so it cannot fail and cannot loop.
const NO_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect width='100%25' height='100%25' fill='%23eef1f4'/%3E%3Ctext x='50%25' y='50%25' font-family='sans-serif' font-size='22' fill='%239aa5b1' text-anchor='middle' dominant-baseline='middle'%3ENo Image%3C/text%3E%3C/svg%3E";



const AddOnsComponent = ({ selectedAddOns = [], updateAddOnQuantity, openCommentModal, addOnComments = {}, addOns = [], currentCurrency }) => {
  const { t } = useTranslation();
  // Policy modal state
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedAddOnPolicy, setSelectedAddOnPolicy] = useState(null);
  // Simplified add-ons data - single services with price and quantity

  // Get currency from addon's own currencyCode, fallback to currentCurrency
  const codeToSymbol = (code) => {
    if (!code) return '';
    const symbols = {
      'INR': '₹', 'USD': '$', 'EUR': '€', 'GBP': '£', 'PKR': 'Rs',
      'PHP': '₱', 'THB': '฿', 'MYR': 'RM', 'SGD': 'S$', 'IDR': 'Rp'
    };
    const upper = String(code).toUpperCase();
    const sym = symbols[upper] || upper;
    return sym.length > 1 ? sym + ' ' : sym;
  };
  const getAddonCurrency = (addOn) => {
    const code = addOn?.currencyCode || currentCurrency?.code || '';
    if (!code) return currentCurrency?.symbol || '';
    return codeToSymbol(code);
  };

  // Safety check: ensure addOns is an array
  if (!addOns || !Array.isArray(addOns) || addOns.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg font-medium">{t('addOns.noAddOnsAvailable')}</p>
        <p className="text-sm">{t('addOns.pleaseCheckBackLater')}</p>
      </div>
    );
  }

  // Filter out invalid addOns before mapping
  const validAddOns = addOns.filter(addOn => addOn && addOn.id);

  if (validAddOns.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p className="text-lg font-medium">{t('addOns.noAddOnsAvailable')}</p>
        <p className="text-sm">{t('addOns.pleaseCheckBackLater')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {validAddOns.map((addOn) => {
        const selectedAddOnItem = selectedAddOns.find(item => item.addOn.id === addOn.id);
        const currentQuantity = selectedAddOnItem?.quantity || 0;
        
        return (
          <div
            key={`addon-${addOn.id}`}
            className={`overflow-hidden transition-all duration-300 rounded-lg 
              ${currentQuantity > 0
                ? 'bg-green-50 border-green-600 shadow-xl shadow-gray-200 border scale-[1.02]'
                : 'border border-gray-100 shadow hover:shadow-lg bg-white hover:border-blue-100'
              }`}
          
          >
            <div className="flex flex-col md:flex-row h-auto">
              {/* Add-on Image */}
              <div className="relative w-full md:w-64 h-40 md:h-56 flex-shrink-0">
                <img
                  src={addOn.image || NO_IMAGE}
                  alt={addOn.name || 'Add-on'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = NO_IMAGE;
                  }}
                />
                {/* <div className="absolute top-3 left-3 bg-black text-white px-2 py-1 rounded text-sm font-medium">
                  {addOn.category}
                </div> */}
                <div className="absolute top-3 right-3 bg-black bg-opacity-70 text-white px-2 py-1 rounded flex items-center space-x-1">
                  <svg className="h-4 w-4 fill-yellow-400 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span className="text-xs">{addOn.category}</span>
                </div>
              </div>

              {/* Add-on Content */}
              <div className="flex-1 flex flex-col justify-between p-4">
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-semibold" style={{ color: 'var(--primary-color, #1e40af)' }}>{addOn.name}</h3>
                    {/* Price moved to top right */}
                    <div className="text-right">
                      <span className="text-2xl font-bold text-gray-900">
                        {getAddonCurrency(addOn)}{(typeof addOn.price === 'number' ? addOn.price : parseFloat(addOn.price) || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{addOn.description}</p>
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                      </svg>
                      <span>{addOn.duration}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span>Category: {addOn.category}</span>
                      {(addOn.guaranteePolicy || addOn.cancellationPolicy) && (
                        <button
                          onClick={() => {
                            setSelectedAddOnPolicy(addOn);
                            setShowPolicyModal(true);
                          }}
                          className="flex items-center space-x-1 text-blue-600 hover:text-blue-700 text-xs font-medium transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <span>{t('addOns.policy')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Bottom section with quantity controls and comment checkbox */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    {/* Comment checkbox */}
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`comment-${addOn.id}`}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={!!addOnComments[addOn.id]}
                        onChange={() => openCommentModal(addOn)}
                      />
                      <label htmlFor={`comment-${addOn.id}`} className="text-sm text-gray-600 cursor-pointer">
                        {t('addOns.addComment')}
                      </label>
                    </div>

                    {/* Quantity controls */}
                    <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => updateAddOnQuantity(addOn.id, 'default', Math.max(0, currentQuantity - 1))}
                        className={`w-8 h-8 rounded-md flex items-center justify-center transition-all duration-200 ${
                          currentQuantity === 0 
                            ? 'bg-gray-200 text-gray-200 cursor-not-allowed' 
                            : 'bg-white text-gray-700 hover:bg-gray-200 hover:text-gray-900 shadow-sm'
                        }`}
                        disabled={currentQuantity === 0}
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="w-12 text-center font-bold text-gray-900 text-lg">{currentQuantity}</span>
                      <button
                        onClick={() => {
                          const maxQty = addOn.maxQty || 5;
                          if (currentQuantity < maxQty) {
                            updateAddOnQuantity(addOn.id, 'default', currentQuantity + 1);
                          } else {
                            alert(`Maximum quantity is ${maxQty} for this add-on.`);
                          }
                        }}
                        disabled={currentQuantity >= (addOn.maxQty || 5)}
                        className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors duration-200 shadow-sm ${
                          currentQuantity >= (addOn.maxQty || 5)
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'avablity-btn text-white hover:bg-blue-700 hover:shadow-md'
                        }`}
                      >
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Show comment if exists */}
                  {addOnComments[addOn.id] && (
                    <div className="mt-3 text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
                      <strong>Comment:</strong> {addOnComments[addOn.id]}
                    </div>
                  )}

                  {/* Selection status - simple check mark */}
                  {/* {currentQuantity > 0 && (
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center space-x-2 text-green-600">
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="font-medium">{currentQuantity} selected</span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-black">{getAddonCurrency(addOn)}{(addOn.price * currentQuantity).toLocaleString()}</p>
                      </div>
                    </div>
                  )} */}
                </div>
              </div>
            </div>
            

          </div>
        );
      })}

      {/* Policy Modal */}
      {showPolicyModal && selectedAddOnPolicy && (
        <div 
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50" 
          onClick={() => {
            setShowPolicyModal(false);
            setSelectedAddOnPolicy(null);
          }}
          style={{ zIndex: 9999 }}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl max-w-md w-full mx-4 max-h-[70vh] overflow-hidden flex flex-col" 
            onClick={(e) => e.stopPropagation()}
            style={{ zIndex: 10000 }}
          >
            {/* Header */}
            <div 
              className="card-head text-white px-4 py-3 rounded-t-lg flex items-center justify-between"
              style={{ backgroundColor: 'var(--primary-color)', background: 'var(--primary-color)' }}
            >
              <h3 className="text-sm font-semibold">Policy - {selectedAddOnPolicy.name}</h3>
              <button
                onClick={() => {
                  setShowPolicyModal(false);
                  setSelectedAddOnPolicy(null);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="p-4 overflow-y-auto flex-1">
              {/* Guarantee Policy */}
              {selectedAddOnPolicy.guaranteePolicy && (
                <div className="mb-4">
                  <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    {t('addOns.guaranteePolicy')}
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedAddOnPolicy.guaranteePolicy}
                  </p>
                </div>
              )}

              {/* Cancellation Policy */}
              {selectedAddOnPolicy.cancellationPolicy && (
                <div className={selectedAddOnPolicy.guaranteePolicy ? "border-t border-gray-200 pt-4" : ""}>
                  <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('addOns.cancellationPolicy')}
                  </h4>
                  <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                    {selectedAddOnPolicy.cancellationPolicy}
                  </p>
                </div>
              )}

              {/* No Policy Message */}
              {!selectedAddOnPolicy.guaranteePolicy && !selectedAddOnPolicy.cancellationPolicy && (
                <div className="text-center py-6">
                  <p className="text-sm text-gray-500">{t('addOns.noPolicyInfo')}</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-4 py-3">
              <button
                onClick={() => {
                  setShowPolicyModal(false);
                  setSelectedAddOnPolicy(null);
                }}
                className="w-full font-semibold py-2 text-sm rounded-lg transition-colors border-2 bg-transparent"
                style={{ borderColor: 'var(--primary-color)', color: 'var(--primary-color)' }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'var(--primary-color)';
                  e.target.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'var(--primary-color)';
                }}
              >
                {t('ratePlan.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOnsComponent;
